import { Seo } from "../components/Seo";
import { PageHero, CtaLink } from "../components/PageHero";
import { Reveal, Eyebrow } from "../components/Reveal";
import { BeforeAfterSlider } from "../components/BeforeAfterSlider";
import { QuoteForm } from "../components/QuoteForm";
import { SuburbLinks } from "../components/SuburbLinks";
import { FinalCta } from "../components/FinalCta";
import { WORK_ITEMS, BUSINESS } from "../content";
import { Check } from "lucide-react";
import { track } from "../lib/track";

const CHECKLIST = [
  "Detailed kitchen clean including cooktop, oven exterior and rangehood",
  "Bathrooms scrubbed top to bottom — showers, baths, basins and toilets",
  "Internal windows, sills and tracks",
  "Skirting boards, doors, switches and fittings wiped down",
  "Floors vacuumed and mopped throughout",
  "Built-in wardrobes and storage cleaned inside and out",
];

export const EndOfLeasePage = () => (
  <>
    <Seo
      title="End of Lease Cleaning Melbourne South East | Pure Bliss Clean"
      description="Detailed end-of-lease cleaning across Melbourne's South East — Carrum Downs, Frankston, Cranbourne, Berwick, Mornington and surrounds. Leave your rental clean, presentable and ready for inspection. Request a free quote."
      path="/end-of-lease-cleaning"
    />
    <PageHero
      eyebrow="End of Lease Cleaning"
      titleLines={["Moving Out?", "Leave The", "Cleaning To Us."]}
      intro={`Moving is stressful enough without spending your final days scrubbing the property. Pure Bliss Clean provides detailed end-of-lease cleaning across ${BUSINESS.region} to help leave your home clean, presentable and ready for inspection.`}
      image="/assets/work/bath-after.webp"
      imageAlt="Bath gleaming after a detailed Pure Bliss end-of-lease clean"
    >
      <Reveal delay={0.4}>
        <div className="mt-8">
          <CtaLink to="#quote" testId="end-of-lease-page-cta" onClick={() => track("cta_click", { label: "end_of_lease_page" })}>Request an End of Lease Quote</CtaLink>
        </div>
      </Reveal>
    </PageHero>

    <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
      <div className="grid gap-14 lg:grid-cols-2">
        <Reveal>
          <Eyebrow>What's Covered</Eyebrow>
          <h2 className="font-display text-3xl font-black text-brand-ink sm:text-4xl">A Detailed Clean, Room By Room</h2>
          <p className="mt-5 max-w-lg text-base leading-relaxed text-slate-600">
            Every property is different, so we tailor each end-of-lease clean to your home and your agent's
            expectations. A typical end-of-lease clean covers:
          </p>
          <ul className="mt-8 space-y-3.5">
            {CHECKLIST.map((item) => (
              <li key={item} className="flex items-start gap-3 text-sm font-semibold text-brand-ink">
                <span className="mt-0.5 rounded-full bg-teal-50 p-1 text-brand-teal"><Check className="h-3.5 w-3.5" /></span>
                {item}
              </li>
            ))}
          </ul>
          <p className="mt-8 rounded-2xl bg-brand-mist p-5 text-sm leading-relaxed text-slate-600">
            Have specific requirements from your property manager or agent? Let us know when you request your quote
            and we'll factor them in.
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <BeforeAfterSlider {...WORK_ITEMS.find((w) => w.id === "cooktop")} />
        </Reveal>
      </div>
    </section>

    <section className="bg-brand-mist">
      <div className="mx-auto max-w-3xl px-6 py-24 lg:py-32">
        <Reveal className="mb-10 text-center">
          <div className="flex justify-center"><Eyebrow>Free Quote</Eyebrow></div>
          <h2 className="font-display text-3xl font-black text-brand-ink sm:text-4xl">Request Your End of Lease Quote</h2>
          <p className="mx-auto mt-4 max-w-md text-slate-600">Tell us about the property and your move-out date, and we'll be in touch.</p>
        </Reveal>
        <div id="quote">
          <QuoteForm defaultService="End of Lease" source="end_of_lease_page" />
        </div>
      </div>
    </section>
    <SuburbLinks />
    <FinalCta />
  </>
);
